import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import Header from "../sections/Header";
import Footer from "../sections/Footer";
import { Reveal } from "../components/Reveal";
import { PROJECTS, fetchProject } from "../data/projects";
import { mediaUrl } from "../lib/media";

const META = (p) => [
  ["Client", p.client],
  ["Show", p.show],
  ["Location", p.location],
  ["Year", p.year],
];

export default function ProjectPage() {
  const { slug } = useParams();
  const [project, setProject] = useState(PROJECTS.find((p) => p.slug === slug) || null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoaded(false);
    fetchProject(slug)
      .then((p) => {
        if (p) setProject(p);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, [slug]);

  // Per-project SEO: title, meta description, JSON-LD structured data so the
  // build's products/services (wall graphics, booth fabrication, 3D props…)
  // are machine-readable for search engines and AI crawlers.
  useEffect(() => {
    if (!project) return undefined;
    const prevTitle = document.title;
    document.title = `${project.title} | Triple O Creative Industries`;
    const keywords = (project.scope || []).join(", ");
    const desc = `${project.hook || project.summary?.slice(0, 140) || ""} ${project.client} — ${keywords}. Built by Triple O Creative Industries, ${project.location || "Southern California"}.`.slice(0, 300);
    let meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute("content") || "";
    if (meta) meta.setAttribute("content", desc);
    const ld = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: project.title,
      description: project.hook || project.summary?.slice(0, 200),
      about: (project.scope || []).map((s) => ({ "@type": "Thing", name: s })),
      keywords,
      author: {
        "@type": "Organization",
        name: "Triple O Creative Industries",
        url: window.location.origin,
      },
      locationCreated: project.location || undefined,
      dateCreated: project.year || undefined,
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.dataset.projectSeo = "true";
    script.textContent = JSON.stringify(ld);
    document.head.appendChild(script);
    return () => {
      document.title = prevTitle;
      if (meta && prevDesc) meta.setAttribute("content", prevDesc);
      script.remove();
    };
  }, [project]);

  if (!project && !loaded) {
    return (
      <div id="top" className="flex min-h-svh items-center justify-center bg-ink">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-paper/50">Loading…</p>
      </div>
    );
  }

  if (!project) {
    return (
      <div id="top" className="flex min-h-svh flex-col bg-ink text-paper">
        <Header />
        <main className="flex flex-1 flex-col items-start justify-center px-4 sm:px-6 lg:px-8">
          <p data-testid="project-not-found" className="font-display text-3xl font-bold uppercase tracking-tight">
            Project not found.
          </p>
          <Link
            to="/"
            data-testid="project-not-found-home-link"
            className="mt-6 font-mono text-xs uppercase tracking-[0.25em] text-acid"
          >
            Back to Triple O
          </Link>
        </main>
      </div>
    );
  }

  return (
    <div id="top" data-testid="project-page" className="relative bg-ink text-paper">
      <div className="noise-overlay" aria-hidden="true" />
      <Header />
      <main className="pt-28 lg:pt-36">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Link
            to="/#work"
            data-testid="project-back-link"
            className="group flex w-fit items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-paper/60 transition-colors duration-300 hover:text-acid"
          >
            <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
            All Work
          </Link>

          {project.sample && (
            <span
              data-testid="project-sample-badge"
              className="mt-8 inline-block bg-acid px-3 py-1.5 font-mono text-[9px] font-bold uppercase tracking-[0.25em] text-ink"
            >
              Sample project — layout preview, content to be replaced
            </span>
          )}

          <Reveal>
            <h1
              data-testid="project-title"
              className="mt-6 font-display text-4xl font-bold uppercase leading-[1.05] tracking-tight text-paper sm:text-6xl lg:text-7xl"
            >
              {project.title}
            </h1>
          </Reveal>
          {project.hook && (
            <Reveal delay={0.1}>
              <p className="mt-6 max-w-2xl text-base leading-relaxed text-fog sm:text-lg">
                {project.hook}
              </p>
            </Reveal>
          )}

          <Reveal delay={0.15}>
            <dl data-testid="project-meta" className="mt-12 grid grid-cols-2 gap-px bg-white/10 lg:grid-cols-4">
              {META(project).map(([k, v]) => (
                <div key={k} className="bg-ink p-5">
                  <dt className="font-mono text-[9px] uppercase tracking-[0.25em] text-paper/40">{k}</dt>
                  <dd className="mt-2 text-sm font-medium text-paper">{v || "—"}</dd>
                </div>
              ))}
            </dl>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="relative mt-14 overflow-hidden border border-white/10">
              {project.video || project.videoMp4 ? (
                <video
                  data-testid="project-hero-video"
                  className="aspect-video w-full object-cover"
                  autoPlay
                  muted
                  loop
                  playsInline
                  poster={mediaUrl(project.poster)}
                >
                  {project.video && <source src={mediaUrl(project.video)} />}
                  {project.videoMp4 && <source src={mediaUrl(project.videoMp4)} />}
                </video>
              ) : (
                <img
                  data-testid="project-hero-image"
                  src={mediaUrl(project.poster)}
                  alt={project.title}
                  className="aspect-video w-full object-cover"
                />
              )}
            </div>
          </Reveal>

          <div className="grid grid-cols-1 gap-12 py-20 lg:grid-cols-12 lg:py-28">
            <Reveal className="lg:col-span-7">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-acid">The Story</p>
              <p data-testid="project-summary" className="mt-6 whitespace-pre-line text-base leading-relaxed text-paper/85 sm:text-lg">
                {project.summary}
              </p>
            </Reveal>
            <Reveal delay={0.15} className="lg:col-span-5">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-acid">What We Built</p>
              <ul data-testid="project-built-list" className="mt-6">
                {(project.built || []).map((b) => (
                  <li key={b} className="flex items-start gap-3 border-t border-white/10 py-3 text-sm text-paper/80 last:border-b">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 bg-acid" aria-hidden="true" />
                    {b}
                  </li>
                ))}
              </ul>
              <p className="mt-10 font-mono text-[10px] uppercase tracking-[0.3em] text-acid">Products &amp; Services</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {(project.scope || []).map((s) => (
                  <span key={s} className="border border-white/15 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-paper/60">
                    {s}
                  </span>
                ))}
              </div>
            </Reveal>
          </div>

          {(project.gallery || []).length > 0 && (
            <div data-testid="project-gallery" className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              {project.gallery.map((g, i) => (
                <Reveal key={g.src || i} delay={0.06 * i}>
                  <div className="group overflow-hidden border border-white/10">
                    {g.type === "video" ? (
                      <video
                        data-testid={`project-gallery-video-${i}`}
                        autoPlay
                        muted
                        loop
                        playsInline
                        style={{ objectPosition: g.pos || "center" }}
                        className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.04]"
                      >
                        <source src={mediaUrl(g.src)} type="video/webm" />
                        {g.mp4 && <source src={mediaUrl(g.mp4)} type="video/mp4" />}
                      </video>
                    ) : (
                      <img
                        src={mediaUrl(g.src)}
                        alt={g.alt || project.title}
                        loading="lazy"
                        style={{ objectPosition: g.pos || "center" }}
                        className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.04]"
                      />
                    )}
                  </div>
                </Reveal>
              ))}
            </div>
          )}

          <Reveal>
            <div className="my-24 border border-acid/40 bg-panel p-10 text-center sm:p-16">
              <p className="font-display text-2xl font-bold uppercase tracking-tight text-paper sm:text-4xl">
                Have a build like this in mind?
              </p>
              <Link
                to="/#contact"
                data-testid="project-cta"
                className="group mt-8 inline-flex items-center gap-3 bg-acid px-8 py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-paper"
              >
                Start a Project
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </Link>
            </div>
          </Reveal>
        </div>
      </main>
      <Footer />
    </div>
  );
}
